import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Form step validation helper
const canProceedToNextStep = (step, formData) => {
  switch (step) {
    case 1: return formData.room_size !== "";
    case 2: return formData.quality_level !== "";
    case 3: return formData.complexity !== "";
    case 4: return true;
    default: return false;
  }
};

const SIZE_OPTIONS = [
  { value: "small", label: "Small", description: "Up to 15m²" },
  { value: "medium", label: "Medium", description: "15-30m²" },
  { value: "large", label: "Large", description: "30-50m²" },
  { value: "xl", label: "Extra Large", description: "50m²+" }
];

const QUALITY_OPTIONS = [
  { value: "budget", label: "Budget", description: "Cost-effective materials, standard finishes" },
  { value: "standard", label: "Standard", description: "Quality materials, good finishes" },
  { value: "premium", label: "Premium", description: "High-end materials, luxury finishes" }
];

const COMPLEXITY_OPTIONS = [
  { value: "simple", label: "Simple", description: "Straightforward layout, minimal structural work" },
  { value: "moderate", label: "Moderate", description: "Some complexity, minor structural changes" },
  { value: "complex", label: "Complex", description: "Complex design, significant structural work" }
];

const EstimatorPage = () => {
  const { projectType } = useParams();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [projectInfo, setProjectInfo] = useState(null);
  const [additionalFeatures, setAdditionalFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  
  const [formData, setFormData] = useState({
    project_type: projectType,
    room_size: "",
    quality_level: "",
    complexity: "",
    additional_features: []
  });

  const totalSteps = 4;

  useEffect(() => {
    const controller = new AbortController();
    
    const fetchData = async () => {
      try {
        const [projectsRes, featuresRes] = await Promise.all([
          axios.get(`${API}/project-types`, { signal: controller.signal }),
          axios.get(`${API}/additional-features`, { signal: controller.signal })
        ]);
        
        const project = projectsRes.data.find(p => p.id === projectType);
        if (!project) {
          toast.error('Project type not found');
          navigate('/');
          return;
        }
        
        setProjectInfo(project);
        setAdditionalFeatures(featuresRes.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          toast.error('Failed to load estimator data');
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    return () => controller.abort();
  }, [projectType, navigate]);

  const handleCalculate = useCallback(async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculate-estimate`, formData);
      navigate('/results', { state: { estimate: response.data, projectInfo } });
    } catch (err) {
      toast.error('Failed to calculate estimate. Please try again.');
      setCalculating(false);
    }
  }, [formData, navigate, projectInfo]);

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleCalculate();
    }
  }, [currentStep, handleCalculate]);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => {
      if (prev > 1) return prev - 1;
      return prev;
    });
  }, []);

  const toggleFeature = useCallback((featureId) => {
    setFormData(prev => ({
      ...prev,
      additional_features: prev.additional_features.includes(featureId)
        ? prev.additional_features.filter(f => f !== featureId)
        : [...prev.additional_features, featureId]
    }));
  }, []);

  const canProceed = canProceedToNextStep(currentStep, formData);

  // Helper to get step indicator styling
  const getStepClass = (step) => {
    if (currentStep > step) return "bg-green-600 text-white";
    if (currentStep === step) return "bg-amber-700 text-white";
    return "bg-stone-200 text-stone-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{projectInfo?.name ? `${projectInfo.name} Estimator` : 'Cost Estimator'} | TJP Carpentry & Construction</title>
      </Helmet>
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Project Header */}
        <div className="mb-12">
          <Link to="/" className="inline-flex items-center gap-2 text-stone-600 hover:text-slate-900 transition-colors mb-4">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-2">
            Estimate Calculator
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-2">
            {projectInfo?.name}
          </h1>
          <p className="text-stone-600">{projectInfo?.description}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${getStepClass(step)}`}
                  data-testid={`step-indicator-${step}`}
                >
                  {currentStep > step ? <Check size={18} /> : step}
                </div>
                {step < 4 && (
                  <div
                    className={`w-16 md:w-32 h-1 mx-2 rounded ${
                      currentStep > step ? "bg-green-600" : "bg-stone-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span>Size</span>
            <span>Quality</span>
            <span>Complexity</span>
            <span>Features</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-xl shadow-stone-200/50 p-8 mb-8">
          {/* Step 1: Room Size */}
          {currentStep === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Size</h2>
              <p className="text-stone-600 mb-8">Select the approximate size of your project area.</p>
              
              <RadioGroup
                value={formData.room_size}
                onValueChange={(value) => setFormData({ ...formData, room_size: value })}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data-testid="room-size-options"
              >
                {SIZE_OPTIONS.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={option.value}
                    className={`flex items-start gap-4 p-6 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.room_size === option.value
                        ? "border-amber-700 bg-amber-50"
                        : "border-stone-200 hover:border-stone-300 hover:bg-stone-50"
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                    <div>
                      <p className="font-bold text-slate-900">{option.label}</p>
                      <p className="text-sm text-stone-500">{option.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 2: Quality Level */}
          {currentStep === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Quality Level</h2>
              <p className="text-stone-600 mb-8">Choose the quality of materials and finishes.</p>
              
              <RadioGroup
                value={formData.quality_level}
                onValueChange={(value) => setFormData({ ...formData, quality_level: value })}
                className="space-y-4"
                data-testid="quality-level-options"
              >
                {QUALITY_OPTIONS.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`quality-${option.value}`}
                    className={`flex items-start gap-4 p-6 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.quality_level === option.value
                        ? "border-amber-700 bg-amber-50"
                        : "border-stone-200 hover:border-stone-300 hover:bg-stone-50"
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={`quality-${option.value}`} className="mt-1" />
                    <div>
                      <p className="font-bold text-slate-900">{option.label}</p>
                      <p className="text-sm text-stone-500">{option.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 3: Complexity */}
          {currentStep === 3 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Project Complexity</h2>
              <p className="text-stone-600 mb-8">How complex is your project design?</p>
              
              <RadioGroup
                value={formData.complexity}
                onValueChange={(value) => setFormData({ ...formData, complexity: value })}
                className="space-y-4"
                data-testid="complexity-options"
              >
                {COMPLEXITY_OPTIONS.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`complexity-${option.value}`}
                    className={`flex items-start gap-4 p-6 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.complexity === option.value
                        ? "border-amber-700 bg-amber-50"
                        : "border-stone-200 hover:border-stone-300 hover:bg-stone-50"
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={`complexity-${option.value}`} className="mt-1" />
                    <div>
                      <p className="font-bold text-slate-900">{option.label}</p>
                      <p className="text-sm text-stone-500">{option.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 4: Additional Features */}
          {currentStep === 4 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Additional Features</h2>
              <p className="text-stone-600 mb-8">Select any additional features you would like (optional).</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="additional-features-options">
                {additionalFeatures.map((feature) => (
                  <Label
                    key={feature.id}
                    htmlFor={feature.id}
                    className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.additional_features.includes(feature.id)
                        ? "border-amber-700 bg-amber-50"
                        : "border-stone-200 hover:border-stone-300 hover:bg-stone-50"
                    }`}
                  >
                    <Checkbox
                      id={feature.id}
                      checked={formData.additional_features.includes(feature.id)}
                      onCheckedChange={() => toggleFeature(feature.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className="font-semibold text-slate-900">{feature.name}</p>
                        <p className="text-amber-700 font-bold text-sm">+£{feature.cost.toLocaleString()}</p>
                      </div>
                    </div>
                  </Label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="h-12 px-6"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 font-semibold shadow-lg shadow-stone-900/10 transition-all hover:-translate-y-0.5"
            data-testid="next-button"
          >
            {currentStep === totalSteps ? "Calculate Estimate" : "Next"}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EstimatorPage;
